import { useMutation, useQueryClient } from '@tanstack/react-query';
import { WIDGET_TYPE } from 'business/constants';
import { WidgetT } from 'business/definitions';
import { patchWidgetsPositionOnDelete } from 'lib/layout/engine/helper/layoutHelpers';
import { deleteWidget, editWidget } from 'lib/layout/provider/backend';
import { ARGUMENT_CONTAINER } from 'routes/BudgetModule/components/BudgetProposal/Resource/Arguments/Argument/hooks/constants';
import { WidgetContent } from 'routes/Dashboard/components/WidgetsLayout/helpers';

const useArgumentWidgetMutations = ({
    isPdfModeActivated,
    layoutId,
}: {
    isPdfModeActivated: boolean;
    layoutId: number | null;
}) => {
    const queryClient = useQueryClient();
    const updateWidgetMutation = useMutation({
        mutationFn: ({
            widget,
            content,
        }: {
            widget: WidgetT;
            content: WidgetContent;
        }) => {
            if (isPdfModeActivated) {
                return Promise.reject(
                    new Error('Cannot edit widget in PDF mode'),
                );
            }
            return editWidget(ARGUMENT_CONTAINER, widget, content);
        },
        onSuccess: (newWidget, { widget }) => {
            if (widget.type === WIDGET_TYPE.chart) {
                // TODO: update API response so we don't have to re-fetch all widgets on title update!
                queryClient.invalidateQueries({
                    queryKey: ['widgets', layoutId],
                });
            } else {
                queryClient.setQueryData(
                    ['widgets', layoutId],
                    (oldWidgets: WidgetT[]) =>
                        oldWidgets.map((oldWidget) =>
                            oldWidget.key === newWidget.key
                                ? newWidget
                                : oldWidget,
                        ),
                );
            }
        },
    });
    const deleteWidgetMutation = useMutation({
        mutationFn: (widget: WidgetT) =>
            deleteWidget(ARGUMENT_CONTAINER, widget.key),
        onSuccess: (_data, widget) => {
            queryClient.setQueryData(
                ['widgets', layoutId],
                (widgets: WidgetT[]) => {
                    const newWidgets = widgets.filter(
                        (item) => item.key !== widget.key,
                    );
                    return patchWidgetsPositionOnDelete(newWidgets, widget);
                },
            );
        },
    });
    return {
        updateWidgetMutation,
        deleteWidgetMutation,
    };
};

export default useArgumentWidgetMutations;
